// src/oura/delivery/PlanView.tsx
//
// Plan, inside Delivery: the 10 key pages Oura suggests (staff confirm or
// swap any), the ledger that proves every old Fixes step landed somewhere,
// and the contract itself. It used to be its own tab.

import { useEffect, useState, type ReactNode } from "react";
import { FileStackIcon, KeyRoundIcon, LightbulbIcon } from "lucide-react";
import type { DeliverySuggestion, KeyPage, KeyPages, LedgerCounts, LedgerRow, SuggestionsPayload } from "@service/aeo/delivery";
import { decideDeliverySuggestion, getKeyPages, getLedger, getSuggestions, putKeyPages, subscribeDelivery } from "@service/aeo/delivery";
import { cn } from "../lib/cn";
import { FoldCard, GroupFold, ListRow, PillButton, Tag, TYPE } from "../ui";
import { FIELD } from "./drawerParts";
import { instantDay } from "./format";

function sourceFact(p: KeyPage): string {
    if (p.source === "home") return "The home page";
    if (p.source === "clicks" && p.clicks != null) return `${p.clicks} Google clicks in 28 days`;
    if (p.source === "links" && p.inlinks != null) return `${p.inlinks} links to it on the site`;
    return "Added by staff";
}

/**
 * "Confirm the 10 key pages", or the number actually listed: a one-page site
 * suggests one page, and a button that says ten over a list of one reads as
 * a mistake.
 */
function confirmLabel(verb: "Confirm" | "Save", n: number): string {
    return n === 1 ? `${verb} the key page` : `${verb} the ${n} key pages`;
}

/**
 * The card's own title, counting what is listed. It said "10 key pages" over a
 * list of one, which is the same mistake the button already avoided (#3989).
 * Unknown (still loading) is named without a number.
 */
function keyPagesTitle(n: number | null): string {
    if (n === null || n === 0) return "Key pages";
    return n === 1 ? "1 key page" : `${n} key pages`;
}

export function KeyPagesCard({ businessId }: { businessId: string }) {
    const [kp, setKp] = useState<KeyPages | null | "loading" | "failed">("loading");
    const [editing, setEditing] = useState(false);
    const [draft, setDraft] = useState<string[]>([]);
    const [swapAt, setSwapAt] = useState<number | null>(null);
    const [typed, setTyped] = useState("");
    const [busy, setBusy] = useState(false);
    const [note, setNote] = useState<{ tone: "ok" | "error"; text: string } | null>(null);

    useEffect(() => {
        let live = true;
        const read = () =>
            getKeyPages(businessId).then((r) => {
                if (!live) return;
                setKp(r.ok ? r.data : "failed");
            });
        void read();
        const off = subscribeDelivery(() => void read());
        return () => {
            live = false;
            off();
        };
    }, [businessId]);

    if (kp === "loading") return <FoldCard id="key-pages" title={keyPagesTitle(null)} icon={{ icon: KeyRoundIcon, area: "site" }}><p className={cn(TYPE.tc, "m-0 text-gray-500")}>Loading…</p></FoldCard>;
    if (kp === "failed" || kp === null) {
        return (
            <FoldCard id="key-pages" title={keyPagesTitle(null)} icon={{ icon: KeyRoundIcon, area: "site" }}>
                <p className={cn(TYPE.tc, "m-0 text-gray-500")}>{kp === null ? "Oura suggests the key pages after the first scan." : "The key pages did not load. Try again in a minute."}</p>
            </FoldCard>
        );
    }

    const pool: KeyPage[] = (kp.suggested ?? []).concat(kp.candidates ?? [], kp.pages ?? []).filter((p, i, all) => all.findIndex((x) => x.url === p.url) === i);
    const byUrl = new Map(pool.map((p): [string, KeyPage] => [p.url, p]));
    const list = editing ? draft : kp.pages.map((p) => p.url);
    const spare = pool.filter((p) => !list.includes(p.url));

    const start = () => {
        setDraft(kp.pages.map((p) => p.url));
        setEditing(true);
        setNote(null);
    };
    const save = async (urls: string[]) => {
        setBusy(true);
        const r = await putKeyPages(businessId, urls);
        setBusy(false);
        if (!r.ok) {
            setNote({ tone: "error", text: r.reason === "rejected" && r.message ? r.message : "That did not save. Try again." });
            return;
        }
        setKp(r.data);
        setEditing(false);
        setSwapAt(null);
        // The count, not the title's 10: a client with 1 confirmed page was told
        // "these 10" and "PageSpeed measures all 10" (#3989).
        const saved = r.data.pages.length;
        setNote({ tone: "ok", text: `Saved. Every key-page check reads ${saved === 1 ? "this page" : `these ${saved}`} from the next check.` });
    };
    const swapTo = (url: string) => {
        if (swapAt === null || !url.trim()) return;
        setDraft((d) => d.map((u, i) => (i === swapAt ? url.trim() : u)));
        setSwapAt(null);
        setTyped("");
    };

    return (
        <FoldCard
            id="key-pages"
            anchor="key-pages"
            title={keyPagesTitle(list.length)}
            icon={{ icon: KeyRoundIcon, area: "site" }}
            foldedFact={kp.confirmed ? `Confirmed by ${kp.confirmedBy ?? "staff"}` : "Not confirmed yet"}
            right={kp.confirmed ? <Tag tone="green">Confirmed</Tag> : <Tag tone="amber">Oura's suggestion</Tag>}
        >
            <p className={cn(TYPE.tb, "m-0 text-gray-600")}>
                {list.length === 0 && !editing
                    ? "Oura has no key pages to suggest yet. It reads them from the site on the next check."
                    : kp.confirmed
                    ? `Confirmed by ${kp.confirmedBy ?? "staff"}${kp.confirmedAt ? ` on ${instantDay(kp.confirmedAt)}` : ""}. Every check except broken pages, dead links and old links reads these pages. PageSpeed measures ${list.length === 1 ? "it" : `all ${list.length}`}.`
                    : "Oura suggests the home page, then the pages with the most Google clicks, then the most linked-to. Confirm them, or swap any page. They apply from the next check."}
            </p>
            <div className="mt-3">
                {list.map((url, i) => {
                    const p = byUrl.get(url) ?? { url, source: "staff" as const };
                    return (
                        <ListRow
                            key={`${url}-${i}`}
                            lead="dot"
                            icon={<span className={cn(TYPE.tm, "w-4 text-right tabular-nums text-gray-400")}>{i + 1}</span>}
                            title={p.title ? `${p.title} · ${url}` : url}
                            meta={sourceFact(p)}
                            right={
                                editing ? (
                                    <button type="button" onClick={() => setSwapAt(i)} className={cn(TYPE.tc, "font-medium text-blue-700 hover:underline")}>
                                        Swap
                                    </button>
                                ) : undefined
                            }
                        />
                    );
                })}
            </div>
            {editing && swapAt !== null && (
                <div className="mt-3 space-y-2 rounded-2xl bg-gray-50 p-3">
                    <p className={cn(TYPE.tc, "m-0 font-medium text-gray-900")}>Swap {list[swapAt]} for:</p>
                    <div className="flex flex-wrap gap-2">
                        {spare.map((p) => (
                            <button key={p.url} type="button" onClick={() => swapTo(p.url)} className={cn(TYPE.tc, "rounded-full bg-white px-3 py-1 ring-1 ring-gray-200 hover:ring-blue-300")}>
                                {p.url} <span className="text-gray-500">· {sourceFact(p)}</span>
                            </button>
                        ))}
                    </div>
                    <div className="flex gap-2">
                        <input className={FIELD} value={typed} onChange={(e) => setTyped(e.target.value)} placeholder="Or type an address, /events" />
                        <PillButton onClick={() => swapTo(typed)} disabled={!typed.trim()}>
                            Use it
                        </PillButton>
                    </div>
                </div>
            )}
            {note && <p className={cn(TYPE.tc, "m-0 mt-3", note.tone === "ok" ? "text-emerald-700" : "text-rose-600")}>{note.text}</p>}
            <div className="mt-3 flex flex-wrap gap-2">
                {/* Nothing listed: nothing to confirm or swap, so no button says "the 0 key pages" (Angel, 15 Oct). */}
                {!editing && !kp.confirmed && kp.pages.length > 0 && (
                    <PillButton variant="primary" busy={busy} onClick={() => void save(kp.pages.map((p) => p.url))}>
                        {confirmLabel("Confirm", kp.pages.length)}
                    </PillButton>
                )}
                {!editing && kp.pages.length > 0 && <PillButton onClick={start}>Swap a page</PillButton>}
                {editing && (
                    <>
                        <PillButton variant="primary" busy={busy} onClick={() => void save(draft)}>
                            {confirmLabel("Save", draft.length)}
                        </PillButton>
                        <PillButton onClick={() => { setEditing(false); setSwapAt(null); }} disabled={busy}>
                            Cancel
                        </PillButton>
                    </>
                )}
            </div>
        </FoldCard>
    );
}

/** Where a kept suggestion lands (H8, H16): said before staff decide. */
const KEPT_LANDS: Record<string, string> = {
    placeholder_pages: "Keep it and it becomes an item under S3 (G2 after month 1).",
    press_page: "Keep it and it takes a page slot under B1 (G1 after month 1).",
    contradiction: "Keep it and it becomes this month's small change, G5.",
};

function decidedFact(sg: DeliverySuggestion): string {
    const word = sg.state === "kept" ? "Kept" : "Dismissed";
    return [word, sg.decidedBy ? `by ${sg.decidedBy}` : null, sg.decidedAt ? instantDay(sg.decidedAt) : null].filter(Boolean).join(" ");
}

/** The last scan's suggestions: staff keep or dismiss each. Fixes decided them until the switch. */
export function SuggestionsCard({ businessId }: { businessId: string }) {
    const [data, setData] = useState<SuggestionsPayload | null | "failed">(null);
    const [busy, setBusy] = useState<string | null>(null);
    const [note, setNote] = useState<{ tone: "ok" | "error"; text: string } | null>(null);

    useEffect(() => {
        let live = true;
        const read = () =>
            getSuggestions(businessId).then((r) => {
                if (live) setData(r.ok ? r.data : "failed");
            });
        void read();
        const off = subscribeDelivery(() => void read());
        return () => {
            live = false;
            off();
        };
    }, [businessId]);

    if (data === null) return null;
    if (data === "failed") {
        return (
            <FoldCard id="suggestions" title="Suggestions" icon={{ icon: LightbulbIcon, area: "site" }}>
                <p className={cn(TYPE.tc, "m-0 text-gray-500")}>The suggestions did not load. Try again in a minute.</p>
            </FoldCard>
        );
    }
    const proposed = data.proposals.filter((p) => p.state === "proposed");
    if (proposed.length === 0 && data.decided.length === 0) return null;

    const decide = async (sg: DeliverySuggestion, decision: "keep" | "dismiss") => {
        setBusy(`${sg.id}:${decision}`);
        setNote(null);
        const r = await decideDeliverySuggestion(businessId, sg.id, decision);
        setBusy(null);
        if (!r.ok) {
            setNote({ tone: "error", text: r.reason === "rejected" && r.message ? r.message : "That did not save. Try again." });
            return;
        }
        setData(r.data);
        setNote({ tone: "ok", text: decision === "keep" ? `Kept: ${sg.action}. It shows in its deliverable's History.` : `Dismissed: ${sg.action}. Oura stops suggesting it.` });
    };

    return (
        <FoldCard
            id="suggestions"
            title="Suggestions"
            icon={{ icon: LightbulbIcon, area: "site" }}
            right={proposed.length ? <Tag tone="blue">{proposed.length} to decide</Tag> : <Tag tone="green">All decided</Tag>}
        >
            <p className={cn(TYPE.tb, "m-0 text-gray-600")}>
                {proposed.length ? "From the last scan. Keep one and it becomes work under its deliverable. Dismiss it and Oura stops suggesting it." : "Nothing from the last scan waits on a decision."}
            </p>
            {proposed.length > 0 && (
                <div className="mt-3">
                    {proposed.map((sg) => (
                        <div key={sg.id} className="flex flex-wrap items-start gap-3 border-t border-gray-100 py-3 first:border-t-0" data-suggestion={sg.id}>
                            <LightbulbIcon aria-hidden size={16} className="mt-0.5 shrink-0 text-blue-600" />
                            <div className="min-w-0 flex-1 basis-56">
                                <p className={cn(TYPE.tb, "m-0 font-medium text-gray-900")}>{sg.action}</p>
                                <p className={cn(TYPE.tc, "m-0 text-gray-600")}>{sg.why}</p>
                                {KEPT_LANDS[sg.rule] && <p className={cn(TYPE.tm, "m-0 mt-1 text-gray-500")}>{KEPT_LANDS[sg.rule]}</p>}
                            </div>
                            <div className="ml-7 flex shrink-0 items-center gap-2 sm:ml-0">
                                <PillButton variant="text" size="sm" busy={busy === `${sg.id}:dismiss`} disabled={busy !== null} onClick={() => void decide(sg, "dismiss")}>
                                    Dismiss
                                </PillButton>
                                <PillButton size="sm" busy={busy === `${sg.id}:keep`} disabled={busy !== null} onClick={() => void decide(sg, "keep")}>
                                    Keep
                                </PillButton>
                            </div>
                        </div>
                    ))}
                </div>
            )}
            {note && <p className={cn(TYPE.tc, "m-0 mt-3", note.tone === "ok" ? "text-emerald-700" : "text-rose-600")}>{note.text}</p>}
            {data.decided.length > 0 && (
                <div className="mt-3">
                    <GroupFold id={`suggestions.${businessId}.decided`} tone="gray" title="Decided" count={data.decided.length} lead="square" defaultFolded>
                        {data.decided.map((sg) => (
                            <ListRow key={sg.id} lead="square" title={sg.action} meta={decidedFact(sg)} />
                        ))}
                    </GroupFold>
                </div>
            )}
        </FoldCard>
    );
}

const LANDS_WORD: Record<LedgerRow["landsAs"], string> = {
    items: "Became items",
    carried: "Carried as done",
    moved: "Moved",
    outside: "Outside the contract",
    retired: "Retired by name",
    unaccounted: "Unaccounted",
};

export function LedgerCard({ businessId }: { businessId: string }) {
    const [data, setData] = useState<{ counts: LedgerCounts; rows: LedgerRow[] } | null | "failed">(null);
    useEffect(() => {
        let live = true;
        getLedger(businessId).then((r) => {
            if (live) setData(r.ok ? r.data : "failed");
        });
        return () => {
            live = false;
        };
    }, [businessId]);
    if (data === null) return null;
    if (data === "failed") return null;
    const c = data.counts;
    if (c.total === 0) return null;
    const groups: Array<LedgerRow["landsAs"]> = ["unaccounted", "carried", "items", "moved", "outside", "retired"];
    return (
        <FoldCard
            id="ledger"
            title="From Fixes"
            icon={{ icon: FileStackIcon, area: "work" }}
            defaultFolded
            // The tag carries the unaccounted count: said once, so a phone header fits.
            foldedFact={`${c.total} old steps`}
            right={c.unaccounted === 0 ? <Tag tone="green">0 unaccounted</Tag> : <Tag tone="rose">{c.unaccounted} unaccounted</Tag>}
        >
            <p className={cn(TYPE.tb, "m-0 text-gray-600")}>
                {c.total} old steps: {c.items} became items, {c.carried} carried as done, {c.moved} moved, {c.outside} outside the contract, {c.retired} retired by name, {c.unaccounted} unaccounted. Every step lands once.
                {c.unaccounted > 0 ? " Fixes stays on for this client until every step has a place." : ""}
            </p>
            <div className="mt-3">
                {groups.map((g) => {
                    const rows = data.rows.filter((r) => r.landsAs === g);
                    if (rows.length === 0) return null;
                    return (
                        <GroupFold
                            key={g}
                            id={`ledger.${businessId}.${g}`}
                            tone={g === "unaccounted" ? "rose" : g === "carried" ? "green" : g === "items" ? "blue" : g === "moved" ? "violet" : "gray"}
                            title={LANDS_WORD[g]}
                            count={rows.length}
                            lead="square"
                            // Unaccounted steps block the switch: always shown open.
                            defaultFolded={g !== "carried" && g !== "unaccounted"}
                        >
                            {rows.map((r, i) => (
                                <ListRow key={`${r.step}-${i}`} lead="square" icon={<span className={cn(TYPE.tm, "font-semibold text-gray-500")}>{r.code ?? ""}</span>} title={r.step} sub={r.where} />
                            ))}
                        </GroupFold>
                    );
                })}
            </div>
        </FoldCard>
    );
}

/**
 * Plan inside Delivery: the suggestions first, since they are the one card that
 * waits on staff, then key pages, the ledger and the contract the workspace renders.
 */
export function PlanView({ businessId, contract }: { businessId: string; contract: ReactNode }) {
    return (
        <div className="space-y-5">
            <SuggestionsCard businessId={businessId} />
            <KeyPagesCard businessId={businessId} />
            <LedgerCard businessId={businessId} />
            {contract}
        </div>
    );
}
